import { Inject, Injectable } from '@angular/core';
import { I18NEXT_SERVICE, I18NextPipe, ITranslationService } from 'angular-i18next';
import { MarkdownService } from 'ngx-markdown';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FlightStatusDateService } from '@modules/flight-status/flight-status-date.service';
import { HttpParams, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from 'env';
import { Cities } from '@modules/flight-status';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { ErrorMessage } from '@modules/flight-status/flight-status';

export interface ErrorNotification {
    helperText: string,
    linkText: string
}

@Injectable({
    providedIn: 'root'
})

export class FlightStatusService {

    private readonly _cityEndPoint = environment.flightStatus.cityEndPoint;
    private _dates: Array<any>;
    private _errorIDs: Array<string> = [];

    constructor(@Inject(I18NEXT_SERVICE) private _i18NextService: ITranslationService,
                private _i18NextPipe: I18NextPipe,
                private _markdownService: MarkdownService,
                private _dateService: FlightStatusDateService,
                private _http : HttpClient,
                private _logger: NGXLogger
                ) {
    }

    get dates() {
        return this._dates;
    }

    set dates(value) {
        this._dates = value;
    }

    get errorIDs() {
        return this._errorIDs;
    }

    set errorIDs(value: Array<string>) {
        this._errorIDs = value;
    }

    public loadNamespaces(): Promise<any> {
        return this._i18NextService.loadNamespaces(['common', 'error']).then(() => {
            const flightStatusObj = this.translate('dateFormats.flightStatus');
            if (flightStatusObj) {
                this.dates = this._dateService.getDates(flightStatusObj);
            }
        });
    }

    public translate(key: string, isMarkdown = false, options = {}): string {
        const opts = { ...options, returnObjects: true, escapeValue: false };
        if (isMarkdown) {
            return this._markdownService.compile(this._i18NextPipe.transform(key, opts));
        }
        return this._i18NextPipe.transform(key, opts);
    }

    getNotificationMessage(errorCount): ErrorNotification {
        this.errorIDs.push('ERRCODE408');
        if (errorCount === 1) {
            return { helperText: this.translate('error:ERRCODE408.messageSingular'), linkText: this.translate('error:ERRCODE408.linkTextSingular')  }
        } else {
            return { helperText: this.translate('error:ERRCODE408.messageSingular'), linkText: ('' + errorCount + ' ' + this.translate('error:ERRCODE408.linkTextPlural')) };
        }
    }

    setErrorMessages(control: AbstractControl, viewChild: any, errorMessage: ErrorMessage) {
        const errors = this.errorIDs;
        viewChild.nativeElement.invalid = false;
        if (control.errors) {
            viewChild.nativeElement.invalid = true;

            if (errorMessage.requiredMessage?.text && control.hasError('required')) {
                viewChild.nativeElement.validityMessage = errorMessage.requiredMessage.text;
                errors.push(errorMessage.requiredMessage.code);
            }
            else if (errorMessage.lengthMessage?.text && (control.hasError('minlength') || control.hasError('maxlength'))) {
                viewChild.nativeElement.validityMessage = errorMessage.lengthMessage.text;
                errors.push(errorMessage.lengthMessage.code);
            }
            else if (errorMessage.invalidAirportMessage?.text && control.hasError('invalidCode')) {
                viewChild.nativeElement.validityMessage = errorMessage.invalidAirportMessage.text;
                errors.push(errorMessage.invalidAirportMessage.code);
            }
            else {
                viewChild.nativeElement.validityMessage = errorMessage.invalidAirportMessage.text;
                errors.push(errorMessage.invalidAirportMessage.code);
            }
        }
        this.errorIDs = errors;
    }

    public searchFlightsbyCities(cities: Cities): Observable<any> {
        const body = new HttpParams()
                .set('searchBy', 'City')
                .set('originAirport', cities.originAirport)
                .set('destinationAirport', cities.destinationAirport)
                .set('travelDate', cities.travelDate)
                .set('clientLocalDate', cities.clientLocalDate);

        return this._http.post(this._cityEndPoint, body, {responseType: 'text'}).pipe(catchError(this._handleError));
    }

    public _handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            this._logger.error('Error occurred:', error.error.message);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong.
            this._logger.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.error}`);
        }
        return throwError(
            'Something went wrong with the http call.');
    }
}
